import React, { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { faPalette, faPlusCircle, faTableColumns } from "@fortawesome/pro-regular-svg-icons";
import { useAppSelector } from "hooks/redux";
import { BuilderToolbarRail } from "components/builderShell/BuilderToolbarRail";
import type { IBuilderToolbarRailOption } from "components/builderShell/types";
import type { TEmailBuilderActivePanel } from "redux/reducers/emailBuilder/emailBuilder";
import type { TSetEmailBuilderActivePanel } from "../hooks/useEmailBuilderPanelSlide";
import { useEmailBuilderViewport } from "../emailBuilderViewportContext";
import { EMAIL_BUILDER_Z_LAYERS } from "../emailBuilderShellZLayers";
import { EmailBuilderSkeleton } from "../EmailBuilderSkeleton";
import "./emailBuilderToolbar.scss";

interface IEmailBuilderToolbarProps {
  closingPanel: boolean;
  setActivePanel: TSetEmailBuilderActivePanel;
}

export const EmailBuilderToolbar = forwardRef<HTMLDivElement, IEmailBuilderToolbarProps>((props, ref) => {
    const { t } = useTranslation();
    const { usesBottomSheetChrome } = useEmailBuilderViewport();
    const activePanel = useAppSelector(store => store.emailBuilderStore.activePanel);
    const loadStatus = useAppSelector(store => store.emailBuilderStore.loadStatus);
    const campaign = useAppSelector(store => store.emailBuilderStore.campaign);
    const draft = useAppSelector(store => store.emailBuilderStore.draft);
    const mode = useAppSelector(store => store.emailBuilderStore.mode);
    const bannerMetaLoading =
      loadStatus !== "succeeded" || campaign === null || draft === null;

    const toolbarMenuOptions: IBuilderToolbarRailOption<TEmailBuilderActivePanel>[] = [
      {
        id: "add-elements",
        tooltip: t("Add elements"),
        shortcutLabel: "/",
        icon: faPlusCircle,
      },
      {
        id: "colors",
        tooltip: t("Customize"),
        icon: faPalette,
      },
      {
        id: "templates",
        tooltip: t("Templates"),
        icon: faTableColumns,
      },
    ];

    if (mode === "preview" || usesBottomSheetChrome) {
      return null;
    }

    if (bannerMetaLoading) {
      return (
        <div
          className="email-builder-toolbar-loading"
          style={{ zIndex: EMAIL_BUILDER_Z_LAYERS.shell.ui }}
          aria-hidden="true"
        >
          <EmailBuilderSkeleton className="email-builder-toolbar-loading__option" />
          <EmailBuilderSkeleton className="email-builder-toolbar-loading__option" />
          <EmailBuilderSkeleton className="email-builder-toolbar-loading__option" />
        </div>
      );
    }

    return (
      <BuilderToolbarRail
        ref={ref}
        style={{ zIndex: EMAIL_BUILDER_Z_LAYERS.shell.ui }}
        role="toolbar"
        aria-label={t("Builder tools")}
        options={toolbarMenuOptions}
        activeId={activePanel}
        noneId="none"
        closing={props.closingPanel}
        onSelect={panel => props.setActivePanel(panel)}
      />
    );
});
EmailBuilderToolbar.displayName = "EmailBuilderToolbar";
